import { ExternalLink, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { QueueFindPayload, QueueIncidentRef, QueueRow, QueueState, QueuesPayload, SystemQueues } from '../api/types';
import { useOpenScreen } from '../components/ScreenLink';
import { Spark } from '../components/Spark';
import { duration } from '../lib/format';
import { toState } from '../lib/queueStates';
import { LoadIssue } from '../components/LoadIssue';
import { readParams, withParam, writeUrl } from '../lib/urlState';
import { useLoad } from '../lib/useLoad';

/** Mirrors checks/infra/queueDepths.js, so the chip here and the incident there agree. */
const WAITING_WARN = 200;
const WAITING_DOWN = 2000;
/** Work older than this with nothing active is a queue nobody is draining. */
const STALL_MS = 5 * 60_000;

// Two queues run at concurrency 1 and carry far more than their name suggests,
// and two of dispatch's behave differently from the rest; the reader should not
// have to know any of that from memory.
const NOTES: Record<string, string> = {
  housekeeping: 'concurrency 1 · carries the watchdog, RC renewals and every purge',
  campaign: 'concurrency 1 · a deep backlog here is a running campaign',
  driver_history_location: 'drops completed jobs on the spot, so “done” is always 0 here',
  gnet_get_location: 'the one dispatch queue without priorities — its backlog is in the wait lane',
};

type Tone = 'ok' | 'warn' | 'danger' | 'muted';
interface QueueStatus {
  label: string;
  tone: Tone;
  why: string;
}

/**
 * One word per queue, and the sentence behind it. The thresholds are the
 * alerting check's, so a row never says "backlog" while the board says
 * nothing -- and "not draining" is the one the numbers alone hide: a modest
 * backlog that nobody has touched for minutes is worse than a large one that
 * is moving.
 */
const statusOf = (queue: QueueRow): QueueStatus => {
  const oldest = queue.oldestWaitingMs ?? 0;
  if (queue.paused) return { label: 'Paused', tone: 'warn', why: 'Workers will not take jobs from it until it is resumed.' };
  if (queue.waiting > 0 && queue.active === 0 && oldest > STALL_MS) {
    return {
      label: 'Not draining',
      tone: 'danger',
      why: `The oldest job has waited ${duration(oldest)} and nothing is active. No worker is taking from this queue.`,
    };
  }
  if (queue.name !== 'campaign' && queue.waiting >= WAITING_DOWN) {
    return { label: 'Backed up', tone: 'danger', why: `${queue.waiting.toLocaleString()} jobs are waiting — far more than a healthy queue holds.` };
  }
  if (queue.name !== 'campaign' && queue.waiting >= WAITING_WARN) {
    return { label: 'Backlog', tone: 'warn', why: `${queue.waiting} jobs are waiting; the check warns from ${WAITING_WARN}.` };
  }
  if (queue.throughput && queue.throughput.failedLastHour > 0 && queue.throughput.failedLastHour * 4 >= Math.max(1, queue.throughput.completedLastHour)) {
    return {
      label: 'Failing',
      tone: 'warn',
      why: `${queue.throughput.failedLastHour} failed in the last hour against ${queue.throughput.completedLastHour} completed.`,
    };
  }
  if (queue.waiting === 0 && queue.active === 0) {
    return {
      label: 'Idle',
      tone: 'muted',
      why: queue.delayed > 0 ? `Nothing to do now; ${queue.delayed} scheduled for later.` : 'Nothing queued and nothing running.',
    };
  }
  return { label: 'Draining', tone: 'ok', why: `${queue.active} running, ${queue.waiting} waiting behind.` };
};

/** The state a queue’s page opens on: the one with something to look at. */
const stateFor = (queue: QueueRow): QueueState => {
  if (queue.waiting === 0 && queue.active === 0 && queue.failed > 0) return 'failed';
  if (queue.waiting === 0 && queue.active > 0) return 'active';
  if (queue.prioritized > 0 && queue.wait === 0) return 'prioritized';
  if (queue.waiting === 0 && queue.delayed > 0) return 'delayed';
  return 'waiting';
};

const incidentTone = (incident: QueueIncidentRef) =>
  incident.severity === 'critical' || incident.severity === 'high' ? 'danger' : 'warn';

/**
 * Every BullMQ queue in both systems, read live from each Redis -- and now
 * readable INTO: a row opens the queue's own page (QueueJobs.tsx) with the
 * jobs in each state and a search within them, and a job-id finder here looks
 * across every queue at once.
 *
 * Still a READ. Bull Board is linked, not embedded: it is a mutating admin UI
 * behind its own basic auth, and framing it would put retry/promote/remove
 * behind this console's permissions instead of its own — in a console whose
 * v1 decision was observe-only. Showing the state here and handing off for the
 * actions keeps that line where it was drawn.
 */
export function Queues() {
  const { data, error, loading, updatedAt, reload } = useLoad(() => api.get<QueuesPayload>('/queues'), [], 10_000);
  // The filter is the URL's, so a reload lands on it again; a queue somebody is
  // reading into has an address of its own.
  const [filter, setFilter] = useState(() => readParams().get('q') ?? '');
  const [findId, setFindId] = useState('');
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState<QueueFindPayload | null>(null);
  const [findError, setFindError] = useState<string | null>(null);
  const openScreen = useOpenScreen();
  const openQueue = (system: string, queue: string, state: QueueState) =>
    openScreen(
      `/queues/${encodeURIComponent(system)}/${encodeURIComponent(queue)}`,
      'queue',
      `state=${state}`,
      `system=${encodeURIComponent(system)}&queue=${encodeURIComponent(queue)}`,
    );

  // A link from when the queue opened in a drawer here (?system=&queue=)
  // still lands on that queue, now on its own page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on arrival
  useEffect(() => {
    const params = readParams();
    const system = params.get('system');
    const queue = params.get('queue');
    if (system && queue) openQueue(system, queue, toState(params.get('state') ?? 'waiting'));
  }, []);

  useEffect(() => {
    writeUrl(withParam(readParams(), 'q', filter.trim() || null));
  }, [filter]);

  const runFind = async () => {
    const id = findId.trim();
    if (!id) return;
    setFinding(true);
    setFindError(null);
    try {
      setFound(await api.get<QueueFindPayload>(`/queues/find?id=${encodeURIComponent(id)}`));
    } catch (caught) {
      setFound(null);
      setFindError((caught as Error).message);
    } finally {
      setFinding(false);
    }
  };

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (!data) return <LoadIssue error={error} updatedAt={null} what="queues" onRetry={() => void reload()} />;

  const needle = filter.trim().toLowerCase();
  const matches = (name: string) => !needle || name.toLowerCase().includes(needle);

  return (
    <>
      <div className="wt-head">
        <h1>Queues</h1>
        <span className="sub">live from each system’s Redis, every 10s</span>
        <span className="wt-spacer" />
        <form
          className="wt-find"
          onSubmit={(event) => {
            event.preventDefault();
            void runFind();
          }}
        >
          <label className="wt-search">
            <Search size={14} />
            <input
              className="input"
              placeholder="find a job by id, in every queue…"
              value={findId}
              onChange={(event) => setFindId(event.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-secondary" disabled={finding || !findId.trim()}>
            {finding ? 'Looking…' : 'Find'}
          </button>
        </form>
      </div>
      <LoadIssue error={error} updatedAt={updatedAt} what="queues" onRetry={() => void reload()} />

      {findError && <div className="wt-banner wt-banner-danger">{findError}</div>}
      {found && (
        <section className="wt-find-results">
          <div className="wt-find-head">
            {found.matches.length === 0 ? (
              <span>
                No queue holds a job <span className="wt-mono">{found.id}</span>
                {found.unreachable.length > 0 && ' — among the systems that could be asked'}.
              </span>
            ) : (
              <span>
                Job <span className="wt-mono">{found.id}</span> found in {found.matches.length} place{found.matches.length === 1 ? '' : 's'}
              </span>
            )}
            <span className="wt-spacer" />
            <button type="button" className="btn btn-ghost" onClick={() => setFound(null)}>
              Dismiss
            </button>
          </div>
          {found.unreachable.map((entry) => (
            <div key={entry.system} className="wt-banner wt-banner-warn">
              {entry.system} could not be asked: {entry.reason}
            </div>
          ))}
          {found.matches.map((match) => (
            <div key={`${match.system}/${match.queue}`} className="wt-find-row">
              <span className="wt-find-where">
                {match.system} / <strong>{match.queue}</strong>
              </span>
              <span className={`wt-status tone-${match.state === 'failed' ? 'danger' : match.state === 'active' ? 'ok' : 'muted'}`}>{match.state}</span>
              <span className="wt-dim">
                {match.job.name ?? 'job'} · queued {match.job.timestamp ? `${duration(Math.max(0, Date.now() - match.job.timestamp))} ago` : '—'}
                {match.job.failedReason && ` · ${match.job.failedReason}`}
              </span>
              <span className="wt-spacer" />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => openQueue(match.system, match.queue, toState(match.state))}
              >
                Open the queue
              </button>
            </div>
          ))}
        </section>
      )}

      <div className="wt-filters" style={{ marginBottom: 14 }}>
        <label className="wt-search">
          <Search size={14} />
          <input className="input" placeholder="filter queues…" value={filter} onChange={(event) => setFilter(event.target.value)} />
        </label>
        {filter && (
          <button type="button" className="btn btn-ghost" onClick={() => setFilter('')}>
            Clear
          </button>
        )}
      </div>

      {data.systems.map((system) => (
        <SystemSection
          key={system.system}
          system={system}
          board={data.bullBoards[system.system] ?? null}
          matches={matches}
          onOpen={(queue) => openQueue(system.system, queue.name, stateFor(queue))}
        />
      ))}

      <details className="wt-glossary">
        <summary>What the columns mean</summary>
        <dl>
          <dt>Status</dt>
          <dd>
            One word per queue. <em>Draining</em> is work moving; <em>Idle</em> is nothing to do; <em>Backlog</em> and{' '}
            <em>Backed up</em> are the alerting check’s own thresholds ({WAITING_WARN} and {WAITING_DOWN} waiting); <em>Not draining</em>{' '}
            is work older than five minutes with nothing active — the one to act on first; <em>Failing</em> is a quarter or more of the
            last hour’s outcomes failing. Hover the chip for the sentence behind it.
          </dd>
          <dt>Waiting</dt>
          <dd>
            Unstarted work in both of BullMQ’s lanes. Jobs enqueued with a priority sit in <em>prioritized</em> and the rest in{' '}
            <em>wait</em>; dispatch-be uses priorities on nearly every queue, so its backlog is almost entirely the former.
          </dd>
          <dt>Oldest</dt>
          <dd>How long the oldest unstarted job has been waiting. A small number that keeps growing is the earliest sign of a stuck worker.</dd>
          <dt>Active</dt>
          <dd>Being processed right now, capped by the worker’s concurrency (1 on housekeeping and campaign).</dd>
          <dt>Delayed</dt>
          <dd>Scheduled for later: retries waiting out their backoff and every scheduler’s next run. A healthy housekeeping queue always has some.</dd>
          <dt>Failed</dt>
          <dd>Out of attempts and kept for seven days, so this only ever climbs within the window. Open the queue to read the reasons.</dd>
          <dt>Done / h</dt>
          <dd>
            Completed in the last hour, with the shape of that hour beside it. Read from BullMQ’s own metrics, which dispatch-be’s workers
            record and sphere-crm’s do not — “not recorded” means unmeasured, not idle.
          </dd>
        </dl>
      </details>
    </>
  );
}

function SystemSection({
  system,
  board,
  matches,
  onOpen,
}: {
  system: SystemQueues;
  board: string | null;
  matches: (name: string) => boolean;
  onOpen: (queue: QueueRow) => void;
}) {
  const shown = system.queues.filter((queue) => matches(queue.name));
  return (
    <section style={{ marginBottom: 24 }}>
      <div className="wt-head" style={{ marginBottom: 8 }}>
        <h1 style={{ fontSize: 15 }}>{system.system}</h1>
        {system.redis && (
          <span className="sub">
            {system.redis.usedMemoryMb} MB · {system.redis.connectedClients} clients
            {system.redis.evictedKeys > 0 && (
              <strong style={{ color: 'var(--danger)' }}> · {system.redis.evictedKeys.toLocaleString()} keys EVICTED — queued jobs are being deleted</strong>
            )}
          </span>
        )}
        {system.incidents.map((incident) => (
          <span key={incident.id} className={`wt-status tone-${incidentTone(incident)}`} title={incident.remedy ?? undefined}>
            {incident.summary ?? incident.checkId}
          </span>
        ))}
        <span className="wt-spacer" />
        {board && (
          <a className="btn btn-secondary" href={board} target="_blank" rel="noreferrer">
            Bull Board <ExternalLink size={13} />
          </a>
        )}
      </div>

      {!system.reachable ? (
        // Stated, not hidden. sphere-watch runs on its own VPS, so either Redis
        // can legitimately be an unopened network hop — and a blank panel would
        // read as "no queues" rather than "not looked at".
        <div className="wt-banner wt-banner-warn">Not reachable: {system.reason}. Nothing here is being watched.</div>
      ) : shown.length === 0 ? (
        <div className="wt-empty" style={{ padding: 24 }}>
          No queue here matches the filter.
        </div>
      ) : (
        <div className="wt-table-wrap">
          <table className="wt-table wt-queues wt-stack">
            <thead>
              <tr>
                <th>Queue</th>
                <th style={{ width: 150 }}>Status</th>
                <th style={{ width: 96, textAlign: 'right' }}>Waiting</th>
                <th style={{ width: 84, textAlign: 'right' }}>Oldest</th>
                <th style={{ width: 72, textAlign: 'right' }}>Active</th>
                <th style={{ width: 76, textAlign: 'right' }}>Delayed</th>
                <th style={{ width: 96, textAlign: 'right' }}>Failed (7d)</th>
                <th style={{ width: 150 }}>Done / h</th>
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {shown.map((queue) => {
                const status = statusOf(queue);
                const worst = queue.incidents[0];
                return (
                  <tr
                    key={queue.name}
                    className="wt-row-click"
                    tabIndex={0}
                    onClick={() => onOpen(queue)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpen(queue);
                      }
                    }}
                  >
                    <td className="wt-stack-wide">
                      <span className="wt-queue-name">{queue.name}</span>
                      {NOTES[queue.name] && <div className="wt-queue-note">{NOTES[queue.name]}</div>}
                    </td>
                    <td className="wt-stack-wide">
                      <span className={`wt-status tone-${status.tone}`} title={status.why}>
                        {status.label}
                      </span>
                      {worst && (
                        <span className={`wt-status tone-${incidentTone(worst)}`} title={worst.summary ?? undefined} style={{ marginLeft: 4 }}>
                          incident
                        </span>
                      )}
                    </td>
                    <td
                      className="wt-mono wt-num"
                      data-label="Waiting"
                      style={status.tone === 'danger' && queue.waiting > 0 ? { color: 'var(--danger)' } : undefined}
                    >
                      {queue.waiting.toLocaleString()}
                      {queue.prioritized > 0 && queue.wait > 0 && (
                        <div className="wt-dim" style={{ fontSize: 11 }}>
                          {queue.prioritized} prioritized
                        </div>
                      )}
                    </td>
                    <td className="wt-mono wt-num" data-label="Oldest">{queue.oldestWaitingMs === null ? '—' : duration(queue.oldestWaitingMs)}</td>
                    <td className="wt-mono wt-num" data-label="Active">{queue.active}</td>
                    <td className="wt-mono wt-num" data-label="Delayed">{queue.delayed}</td>
                    <td className="wt-mono wt-num" data-label="Failed (7d)" style={queue.failed > 0 ? { color: 'var(--warn)' } : undefined}>
                      {queue.failed.toLocaleString()}
                    </td>
                    <td className="wt-stack-wide" data-label="Done / h">
                      {queue.throughput ? (
                        <span className="wt-throughput" title={`${queue.throughput.completedLastHour} completed and ${queue.throughput.failedLastHour} failed in the last ${queue.throughput.minutes} minutes`}>
                          <Spark values={queue.throughput.perMinute} title="completed per minute, last hour" />
                          <b className="wt-mono">{queue.throughput.completedLastHour.toLocaleString()}</b>
                          {queue.throughput.failedLastHour > 0 && <span className="wt-dim"> · {queue.throughput.failedLastHour} failed</span>}
                        </span>
                      ) : (
                        <span className="wt-dim" title="This worker records no BullMQ metrics, so throughput is unmeasured here — not zero">
                          not recorded
                        </span>
                      )}
                    </td>
                    {/* A phone opens the queue by tapping its card. */}
                    <td className="wt-stack-hide">
                      <button type="button" className="btn btn-ghost" onClick={(event) => { event.stopPropagation(); onOpen(queue); }}>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
